import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { UserPlus, CheckCircle } from 'lucide-react';
import { Button, Input } from '@/components/ui';
import { portalService } from '@/services/portalService';
import { setTokens } from '@/services/tokenStore';
import type { SelfRegisterRequest } from '@/types/portal';
import styles from './PatientRegisterPage.module.css';

/**
 * Validates a CPF string using the check digit algorithm.
 * Returns true if the CPF is valid, false otherwise.
 */
function isValidCpf(cpf: string): boolean {
  const digits = cpf.replace(/\D/g, '');
  if (digits.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(digits)) return false;

  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(digits.charAt(i)) * (10 - i);
  }
  let remainder = (sum * 10) % 11;
  if (remainder === 10) remainder = 0;
  if (remainder !== parseInt(digits.charAt(9))) return false;

  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(digits.charAt(i)) * (11 - i);
  }
  remainder = (sum * 10) % 11;
  if (remainder === 10) remainder = 0;
  if (remainder !== parseInt(digits.charAt(10))) return false;

  return true;
}

/**
 * Applies a CPF mask to a string of digits: 000.000.000-00.
 */
function applyCpfMask(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9)
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

/**
 * Applies a phone mask: (00) 00000-0000 or (00) 0000-0000.
 */
function applyPhoneMask(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 2) return digits;
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10)
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

/**
 * Public patient self-registration page.
 *
 * Mobile-first registration form with CPF validation (check digit algorithm),
 * input masks, and clinic branding. No authentication required.
 *
 * Fields: name, CPF, birth date, email, phone, health plan (optional).
 * On submit: calls selfRegister API -> displays success message.
 */
export function PatientRegisterPage() {
  const navigate = useNavigate();

  const [formName, setFormName] = useState('');
  const [formCpf, setFormCpf] = useState('');
  const [formBirthDate, setFormBirthDate] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPhone, setFormPhone] = useState('');
  const [formPassword, setFormPassword] = useState('');
  const [formHealthPlanName, setFormHealthPlanName] = useState('');
  const [formHealthPlanNumber, setFormHealthPlanNumber] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const registerMutation = useMutation({
    mutationFn: (data: SelfRegisterRequest) => portalService.selfRegister(data),
    onSuccess: (authResponse) => {
      setTokens(authResponse.accessToken, authResponse.refreshToken);
      setIsSuccess(true);
    },
    onError: () => {
      setFormError('Erro ao realizar cadastro. Tente novamente.');
    },
  });

  const handleCpfChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormCpf(applyCpfMask(e.target.value));
    },
    [],
  );

  const handlePhoneChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormPhone(applyPhoneMask(e.target.value));
    },
    [],
  );

  const handleSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      setFormError(null);

      if (!formName.trim()) {
        setFormError('Nome completo é obrigatório.');
        return;
      }

      const cpfDigits = formCpf.replace(/\D/g, '');
      if (!cpfDigits) {
        setFormError('CPF é obrigatório.');
        return;
      }
      if (!isValidCpf(cpfDigits)) {
        setFormError('CPF inválido. Verifique os dígitos informados.');
        return;
      }

      if (!formBirthDate) {
        setFormError('Data de nascimento é obrigatória.');
        return;
      }

      if (!formEmail.trim()) {
        setFormError('E-mail é obrigatório para criar sua conta.');
        return;
      }

      if (formPassword.length < 8) {
        setFormError('A senha deve ter no mínimo 8 caracteres.');
        return;
      }

      const payload: SelfRegisterRequest = {
        name: formName.trim(),
        cpf: cpfDigits,
        password: formPassword,
        birthDate: formBirthDate,
        email: formEmail.trim(),
        phone: formPhone.replace(/\D/g, '') || undefined,
        healthPlanName: formHealthPlanName.trim() || undefined,
        healthPlanNumber: formHealthPlanNumber.trim() || undefined,
      };

      registerMutation.mutate(payload);
    },
    [
      formName,
      formCpf,
      formBirthDate,
      formEmail,
      formPhone,
      formPassword,
      formHealthPlanName,
      formHealthPlanNumber,
      registerMutation,
    ],
  );

  const handleGoToDashboard = useCallback(() => {
    navigate('/portal/dashboard');
  }, [navigate]);

  if (isSuccess) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.successState} role="status">
            <CheckCircle size={48} className={styles.successIcon} aria-hidden="true" />
            <h1 className={styles.successTitle}>Cadastro realizado!</h1>
            <p className={styles.successMessage}>
              Seu cadastro foi realizado com sucesso. Você já pode acessar seu painel
              e agendar seus procedimentos.
            </p>
            <Button onClick={handleGoToDashboard} fullWidth>
              Ir para meu painel
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <header className={styles.header}>
          <div className={styles.logo}>
            <span className={styles.logoIcon} aria-hidden="true">+</span>
            <span className={styles.logoText}>
              CRM<span className={styles.logoAccent}>Clinica</span>
            </span>
          </div>
          <h1 className={styles.title}>Cadastro de Paciente</h1>
          <p className={styles.subtitle}>
            Preencha seus dados para se cadastrar na clínica.
          </p>
        </header>

        <form onSubmit={handleSubmit} className={styles.form} noValidate>
          <Input
            label="Nome completo"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            placeholder="Seu nome completo"
            required
            fullWidth
          />

          <div className={styles.formRow}>
            <Input
              label="CPF"
              value={formCpf}
              onChange={handleCpfChange}
              placeholder="000.000.000-00"
              inputMode="numeric"
              required
              fullWidth
            />
            <Input
              label="Data de nascimento"
              type="date"
              value={formBirthDate}
              onChange={(e) => setFormBirthDate(e.target.value)}
              required
              fullWidth
            />
          </div>

          <Input
            label="E-mail"
            type="email"
            value={formEmail}
            onChange={(e) => setFormEmail(e.target.value)}
            placeholder="seu@email.com"
            required
            fullWidth
          />

          <Input
            label="Senha"
            type="password"
            value={formPassword}
            onChange={(e) => setFormPassword(e.target.value)}
            placeholder="Mínimo 8 caracteres"
            required
            fullWidth
          />

          <Input
            label="Telefone (opcional)"
            value={formPhone}
            onChange={handlePhoneChange}
            placeholder="(00) 00000-0000"
            inputMode="tel"
            fullWidth
          />

          <fieldset className={styles.fieldset}>
            <legend className={styles.fieldsetLegend}>
              Convenio (opcional)
            </legend>
            <div className={styles.formRow}>
              <Input
                label="Nome do convenio"
                value={formHealthPlanName}
                onChange={(e) => setFormHealthPlanName(e.target.value)}
                placeholder="Nome do convenio"
                fullWidth
              />
              <Input
                label="Número da carteira"
                value={formHealthPlanNumber}
                onChange={(e) => setFormHealthPlanNumber(e.target.value)}
                placeholder="Número"
                fullWidth
              />
            </div>
          </fieldset>

          {formError && (
            <p className={styles.formError} role="alert">
              {formError}
            </p>
          )}

          <Button
            type="submit"
            fullWidth
            isLoading={registerMutation.isPending}
            leftIcon={<UserPlus size={18} />}
          >
            Cadastrar
          </Button>
        </form>
      </div>
    </div>
  );
}
